import {
  AlertCircle,
  CheckCircle,
  Clock,
  ExternalLink,
  Globe,
  Loader2,
  Lock,
  Plus,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { notify } from "../../lib/notify";
import { CustomDomain } from "../../types";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { CopyButton } from "../ui/copy-button";
import { ExpandableIconButton } from "../ui/expandable-icon-button";
import { Input } from "../ui/input";

// WHY env var: the CNAME target should be a stable hostname (e.g. edge.avahana.ai)
// not a raw IP. If the server moves, only this env var needs updating — users'
// DNS records stay valid because they point to the hostname, not the IP.
const PLATFORM_EDGE_HOSTNAME =
  process.env.REACT_APP_PLATFORM_EDGE_HOSTNAME || "edge.avahana.ai";

const DOMAIN_RE =
  /^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

function clientValidate(d: string): string | null {
  const v = d.trim().toLowerCase();
  if (!v) return "Domain is required";
  if (v.startsWith("http://") || v.startsWith("https://"))
    return 'Enter the hostname only — without "https://"';
  if (!DOMAIN_RE.test(v))
    return "Enter a valid domain name (e.g. app.taskado.ai)";
  return null;
}

const ERROR_REASON_HELP: Record<string, { title: string; hint: string }> = {
  CADDY_ROUTE_MISSING: {
    title: "Routing entry missing",
    hint: "The platform route for this domain disappeared (likely a server restart). It has been automatically re-registered — wait 30 seconds and retry.",
  },
  CERT_NOT_ISSUED: {
    title: "TLS certificate not issued",
    hint: "Ensure your DNS record points here and Cloudflare proxy is off (grey cloud icon).",
  },
  UPSTREAM_NOT_REACHABLE: {
    title: "Service not responding",
    hint: "The service behind this domain is down. Check that it is running in the platform.",
  },
};

function formatCheckedAt(checkedAt?: string): string | null {
  if (!checkedAt) return null;
  const diff = Math.floor((Date.now() - new Date(checkedAt).getTime()) / 60000);
  if (diff < 1) return "Last checked just now";
  if (diff === 1) return "Last checked 1 min ago";
  if (diff < 60) return `Last checked ${diff} min ago`;
  const hours = Math.floor(diff / 60);
  return `Last checked ${hours}h ago`;
}

function DomainStatusBadge({ d }: { d: CustomDomain }) {
  if (d.status === "active" && d.tls_status === "issued")
    return (
      <Badge className="bg-success-bg text-success-fg border-transparent hover:bg-success-bg">
        Active
      </Badge>
    );
  if (d.status === "active")
    return <Badge variant="secondary">Active · TLS pending</Badge>;
  if (d.status === "error")
    return <Badge variant="destructive">Error</Badge>;
  return <Badge variant="outline">Pending DNS</Badge>;
}

interface DomainsTabProps {
  serviceId: string;
  appSubdomain?: string;
  initialDomains?: CustomDomain[];
  onDomainsChange?: (domains: CustomDomain[]) => void;
}

export function DomainsTab({ serviceId, appSubdomain, initialDomains, onDomainsChange }: DomainsTabProps) {
  const [domains, setDomains] = useState<CustomDomain[]>(initialDomains ?? []);
  const [loading, setLoading] = useState(!initialDomains);
  const [newDomain, setNewDomain] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [removingDomain, setRemovingDomain] = useState<string | null>(null);

  useEffect(() => {
    if (initialDomains) {
      setDomains(initialDomains);
    }
  }, [initialDomains]);

  function syncDomains(updated: CustomDomain[]) {
    setDomains(updated);
    onDomainsChange?.(updated);
  }

  async function handleAdd() {
    const err = clientValidate(newDomain);
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError(null);
    setAdding(true);
    try {
      const data = await api.domains.add(
        serviceId,
        newDomain.trim().toLowerCase()
      );
      syncDomains([data.domain, ...domains]);
      setNewDomain("");
      notify.success("Domain added", {
        description: `Point your CNAME to ${PLATFORM_EDGE_HOSTNAME} and TLS will activate automatically.`,
      });
    } catch (err: any) {
      notify.error("Failed to add domain", {
        description: err?.message || "Please try again.",
      });
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(domain: string) {
    setRemovingDomain(domain);
    try {
      await api.domains.remove(serviceId, domain);
      syncDomains(domains.filter((d) => d.domain !== domain));
      notify.success("Domain removed");
    } catch (err: any) {
      notify.error("Failed to remove domain", {
        description: err?.message,
      });
    } finally {
      setRemovingDomain(null);
    }
  }

  return (
    <div className="space-y-4 pt-4">
      {/* Platform URL — permanent HTTPS subdomain, always shown when available */}
      {appSubdomain && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Platform URL
            </CardTitle>
            <CardDescription>
              Permanent URL assigned by Avahana — works even without a custom domain.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/40 px-4 py-3">
              <a
                href={`https://${appSubdomain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-sm font-medium hover:underline truncate"
              >
                https://{appSubdomain}
              </a>
              <div className="flex items-center gap-1.5 shrink-0">
                <CopyButton
                  value={`https://${appSubdomain}`}
                  variant="outline"
                  expandOnHover
                  successMessage="Copied"
                />
                <ExpandableIconButton
                  icon={ExternalLink}
                  label="Open"
                  href={`https://${appSubdomain}`}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Domain list — shown before add form so you see existing domains first */}
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : domains.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Custom Domains
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {domains.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center justify-between px-4 py-3 gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {d.status === "active" && d.tls_status === "issued" ? (
                      <CheckCircle className="h-4 w-4 text-success shrink-0" />
                    ) : d.status === "error" ? (
                      <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
                    ) : (
                      <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="font-mono text-sm font-medium truncate">
                        {d.domain}
                      </p>
                      {d.status === "error" && d.error_reason && ERROR_REASON_HELP[d.error_reason] ? (
                        <div className="space-y-0.5">
                          <p className="text-xs text-destructive font-medium">
                            {ERROR_REASON_HELP[d.error_reason].title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {ERROR_REASON_HELP[d.error_reason].hint}
                          </p>
                        </div>
                      ) : d.error_message ? (
                        <p className="text-xs text-destructive truncate">
                          {d.error_message}
                        </p>
                      ) : null}
                      {formatCheckedAt(d.checked_at) && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {formatCheckedAt(d.checked_at)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <DomainStatusBadge d={d} />
                    {d.tls_status === "issued" && (
                      <Badge
                        variant="outline"
                        className="text-success-fg border-transparent bg-success-bg text-xs"
                      >
                        TLS ✓
                      </Badge>
                    )}
                    <CopyButton
                      value={`https://${d.domain}`}
                      variant="ghost"
                      expandOnHover
                      successMessage="Domain URL copied"
                    />
                    <ExpandableIconButton
                      icon={ExternalLink}
                      label="Open"
                      href={`https://${d.domain}`}
                      variant="ghost"
                    />
                    <ExpandableIconButton
                      icon={removingDomain === d.domain ? Loader2 : Trash2}
                      label="Remove"
                      variant="ghost-destructive"
                      loading={removingDomain === d.domain}
                      disabled={removingDomain === d.domain}
                      onClick={() => handleRemove(d.domain)}
                      aria-label={`Remove ${d.domain}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Add domain form — always shown below any existing domains */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {domains.length === 0 ? "Custom Domains" : "Add Another Domain"}
          </CardTitle>
          {domains.length === 0 && (
            <CardDescription>
              Point your own domain to this service. Add the domain below, then
              create a CNAME record with your DNS provider.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* DNS instruction */}
          <div className="rounded-lg bg-muted/40 p-4 space-y-2 text-sm">
            <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground">
              DNS record to add
            </p>
            <div className="grid grid-cols-[64px_1fr_1fr] gap-x-4 gap-y-1 font-mono text-xs">
              <span className="text-muted-foreground">Type</span>
              <span className="text-muted-foreground">Name</span>
              <span className="text-muted-foreground">Value</span>
              <span className="font-semibold">CNAME</span>
              <span className="text-muted-foreground italic">your-domain</span>
              <div className="flex items-center gap-1 min-w-0">
                <span className="truncate font-semibold">
                  {PLATFORM_EDGE_HOSTNAME}
                </span>
                <CopyButton
                  value={PLATFORM_EDGE_HOSTNAME}
                  size="icon"
                  variant="ghost"
                  className="h-5 w-5 shrink-0"
                  successMessage="Copied"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground pt-1">
              Using a root domain (e.g.{" "}
              <span className="font-mono">taskado.ai</span>)? Use an A record
              pointing to the server IP, or enable CNAME flattening in
              Cloudflare.
            </p>
          </div>

          {/* Add domain input */}
          <div className="space-y-1.5">
            <div className="flex gap-2">
              <Input
                placeholder="app.taskado.ai"
                value={newDomain}
                onChange={(e) => {
                  setNewDomain(e.target.value);
                  setValidationError(null);
                }}
                onKeyDown={(e) => e.key === "Enter" && !adding && handleAdd()}
                className="font-mono text-sm"
              />
              <Button
                onClick={handleAdd}
                disabled={adding || !newDomain.trim()}
                className="shrink-0"
              >
                {adding ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                <span className="ml-1.5">Add</span>
              </Button>
            </div>
            {validationError && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3 shrink-0" />
                {validationError}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
